/**
 * 请求日志（Alice 页面 logs 的复刻）：调用明细列表、用量看板、日期时间筛选、详情弹窗、JSONL 导出与历史对话下载。
 *
 * 背景：数据在网关库 usage_records，经 /control/admin/logs* 读取；运营与客服都能查，导出要管理员并写审计。
 *   Alice 把明细和看板放在同一页用开关切换，筛选共用，本页保持这个结构（#panel-logs 加 log-view-dashboard 类切换）。
 * 设计意图：
 *   - 筛选分「草稿」与「已应用」两份：输入时只改草稿，点「筛选」或回车才查询，翻页与切视图沿用已应用的条件。
 *   - 起止时间用浏览器本地时区选择，转成 UTC ISO 才发给后端；看板的自然日分桶带 tz_offset。
 *   - 视图、模型口径、趋势指标记在 localStorage，刷新后保持。
 *   - 只渲染当前视图，不再用 CSS 隐藏另一个视图，避免看板图表在隐藏容器里量不到尺寸。
 * 关键约束：慢响应用序号丢弃，防止旧筛选的结果覆盖新结果；汇总卡只有指定用户时才有（后端不为汇总扫全表）。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { DashDimension, LogDashboard, LogList, LogQuery, LogRecord } from '@ap/api-client/admin-runtime'
import { runtimeApi, saveBlob } from '@ap/api-client/admin-runtime'
import { AG_runtime as G, errorTextRuntime } from '@ap/i18n/admin-runtime'
import { useI18n } from '@ap/i18n'
import { fmtDateTime, useToast } from '@ap/ui'
import { useAdmin } from '../../session.tsx'
import { Stat, TableWrap } from '../../alice/kit.tsx'
import {
  DateTimeField,
  ExportDialog,
  SearchInput,
  dayEndIso,
  dayStartIso,
  fmtInt,
  fmtLatency,
  fmtPct,
  fmtTokens,
  localOffsetMinutes,
  localToIso,
  useMoney,
  type ExportParams,
} from '../../alice/kit-runtime.tsx'
import { EmptyRow, PageNav, ellipsis } from '../../alice/kit-runtime2.tsx'
import { LogDashboard as Dashboard, type DashMetric } from './runtime/LogDashboard.tsx'
import { LogDetailModal } from './runtime/LogDetailModal.tsx'
import { VendorDialog } from './runtime/VendorDialog.tsx'

const PAGE = 50
const LS = { view: 'ap.runtime.logView', dim: 'ap.runtime.logDim', metric: 'ap.runtime.logMetric' }

interface Filters {
  user: string
  model: string
  source: string
  start: string
  end: string
}
const EMPTY: Filters = { user: '', model: '', source: '', start: '', end: '' }

function saved<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = window.localStorage.getItem(key)
    return allowed.includes(v as T) ? (v as T) : fallback
  } catch {
    return fallback
  }
}
function remember(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch (e) {
    // 隐私模式下写不进去不影响使用，只是刷新后不记得视图
    console.warn('[logs] 保存视图偏好失败', e)
  }
}

function toQuery(f: Filters): LogQuery {
  return {
    user_id: f.user.trim() || undefined,
    model: f.model.trim() || undefined,
    source: f.source.trim() || undefined,
    start_time: localToIso(f.start),
    end_time: localToIso(f.end),
  }
}

export function Logs() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const money = useMoney()

  const [view, setView] = useState<'list' | 'dashboard'>(() => saved(LS.view, ['list', 'dashboard'] as const, 'list'))
  const [dim, setDim] = useState<DashDimension>(() => saved(LS.dim, ['shell', 'upstream'] as const, 'shell'))
  const [metric, setMetric] = useState<DashMetric>(() => saved(LS.metric, ['requests', 'tokens', 'user_cost'] as const, 'requests'))
  const [draft, setDraft] = useState<Filters>(EMPTY)
  const [applied, setApplied] = useState<Filters>(EMPTY)
  const [offset, setOffset] = useState(0)

  const [list, setList] = useState<LogList | null>(null)
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState('')
  const [dash, setDash] = useState<LogDashboard | null>(null)
  const [dashLoading, setDashLoading] = useState(false)
  const [dashError, setDashError] = useState('')

  const [detailId, setDetailId] = useState<string | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [vendorOpen, setVendorOpen] = useState(false)
  const listSeq = useRef(0)
  const dashSeq = useRef(0)

  const loadList = useCallback(async () => {
    const my = ++listSeq.current
    setListLoading(true)
    try {
      const d = await runtimeApi.logs({ ...toQuery(applied), limit: PAGE, offset })
      if (my !== listSeq.current) return
      setList(d)
      setListError('')
    } catch (e) {
      console.error('[logs] 列表加载失败', e)
      if (my !== listSeq.current) return
      setListError(errorTextRuntime(e, locale))
      toast.error(errorTextRuntime(e, locale))
    } finally {
      if (my === listSeq.current) setListLoading(false)
    }
  }, [applied, offset, locale, toast])

  const loadDash = useCallback(async () => {
    const my = ++dashSeq.current
    setDashLoading(true)
    try {
      const d = await runtimeApi.logDashboard({ ...toQuery(applied), dimension: dim, tz_offset: localOffsetMinutes() })
      if (my !== dashSeq.current) return
      setDash(d)
      setDashError('')
    } catch (e) {
      console.error('[logs] 看板加载失败', e)
      if (my !== dashSeq.current) return
      setDashError(errorTextRuntime(e, locale))
      toast.error(errorTextRuntime(e, locale))
    } finally {
      if (my === dashSeq.current) setDashLoading(false)
    }
  }, [applied, dim, locale, toast])

  useEffect(() => {
    if (view === 'list') void loadList()
  }, [view, loadList])
  useEffect(() => {
    if (view === 'dashboard') void loadDash()
  }, [view, loadDash])

  const apply = (next: Filters) => {
    for (const [value, label] of [[next.start, t(G.logs.start)], [next.end, t(G.logs.end)]] as const) {
      if (value && !localToIso(value)) return toast.error(t(G.logs.badTime, { label }))
    }
    const a = localToIso(next.start)
    const b = localToIso(next.end)
    if (a && b && a > b) return toast.error(t(G.logs.badRange))
    setDraft(next)
    setOffset(0)
    setApplied(next)
  }
  const pickView = (v: 'list' | 'dashboard') => {
    setView(v)
    remember(LS.view, v)
  }

  const doExport = async (p: ExportParams) => {
    const r = await runtimeApi.exportLogs({
      user_id: applied.user.trim() || undefined,
      model: applied.model.trim() || undefined,
      source: applied.source.trim() || undefined,
      start_date: dayStartIso(p.startDate),
      end_date: dayEndIso(p.endDate),
      limit: p.maxRows,
      with_bodies: p.withBodies,
    })
    saveBlob(r.blob, r.info.filename)
    setExportOpen(false)
    if (r.info.truncated) toast.error(t(G.exportDialog.truncated, { rows: fmtInt(r.info.rows), matched: fmtInt(r.info.matched) }))
    else toast.ok(t(G.exportDialog.done))
  }

  const s = list?.summary
  const rows = list?.logs ?? []
  const total = list?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE))

  return (
    <div id="panel-logs" className={`panel active ops-panel${view === 'dashboard' ? ' log-view-dashboard' : ''}`}>
      <div className="ops-page-head">
        <div>
          <div className="ops-eyebrow">{t(G.logs.eyebrow)}</div>
          <h1>{t(G.logs.title)}</h1>
          <p>{t(G.logs.desc)}</p>
        </div>
        <div className="period-switch" id="logViewTabs" role="tablist" aria-label={t(G.logs.viewTabs)}>
          <button type="button" className={view === 'list' ? 'active' : ''} onClick={() => pickView('list')}>
            {t(G.logs.viewList)}
          </button>
          <button type="button" className={view === 'dashboard' ? 'active' : ''} onClick={() => pickView('dashboard')}>
            {t(G.logs.viewDashboard)}
          </button>
        </div>
      </div>

      <div className="ops-toolbar">
        <SearchInput width={200} placeholder={t(G.logs.phUser)} value={draft.user} onChange={(e) => setDraft({ ...draft, user: e.target.value })} onEnter={() => apply(draft)} />
        <SearchInput width={150} placeholder={t(G.logs.phModel)} value={draft.model} onChange={(e) => setDraft({ ...draft, model: e.target.value })} onEnter={() => apply(draft)} />
        <SearchInput width={130} placeholder={t(G.logs.phSource)} value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value })} onEnter={() => apply(draft)} />
        <DateTimeField prefix={t(G.logs.start)} ariaLabel={t(G.logs.startAria)} value={draft.start} onChange={(v) => setDraft((d) => ({ ...d, start: v }))} />
        <DateTimeField prefix={t(G.logs.end)} ariaLabel={t(G.logs.endAria)} value={draft.end} onChange={(v) => setDraft((d) => ({ ...d, end: v }))} />
        <button type="button" className="btn btn-primary btn-sm" onClick={() => apply(draft)}>
          {t(G.common.filter)}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => apply(EMPTY)}>
          {t(G.common.clear)}
        </button>
        {view === 'list' && <span className="ops-toolbar-count">{t(G.common.totalRows, { n: fmtInt(total) })}</span>}
        {view === 'list' && list && <span className="ops-toolbar-count">{t(G.logs.sizeBadge, { gb: list.total_size_gb.toFixed(2) })}</span>}
        <button type="button" className="btn btn-secondary btn-sm" disabled={!isAdmin} title={isAdmin ? t(G.logs.exportTip) : t(G.common.adminOnly)} onClick={() => setExportOpen(true)}>
          {t(G.logs.exportJsonl)}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={!isAdmin} title={isAdmin ? t(G.logs.vendorTip) : t(G.common.adminOnly)} onClick={() => setVendorOpen(true)}>
          {t(G.logs.vendor)}
        </button>
      </div>

      {view === 'list' ? (
        <>
          {s ? (
            <div className="stats-grid">
              <Stat label={t(G.logs.sumRequests)} value={fmtInt(s.requests)} sub={t(G.logs.sumStatus, { ok: fmtInt(s.success_requests), fail: fmtInt(s.failed_requests) })} />
              <Stat
                label={t(G.logs.sumTokens)}
                value={fmtTokens(s.input_tokens + s.output_tokens)}
                sub={t(G.logs.sumTokenDetail, { i: fmtTokens(s.input_tokens), o: fmtTokens(s.output_tokens), c: fmtTokens(s.cached_tokens) })}
              />
              <Stat label={t(G.logs.sumCharge)} value={money.petals(s.user_cost)} sub={s.billed_requests > 0 ? t(G.logs.sumBilled, { n: fmtInt(s.billed_requests) }) : t(G.logs.noBilled)} />
              <Stat
                label={t(G.logs.sumUpstream)}
                value={s.known_cost_requests > 0 ? money.usd(s.known_upstream_cost, 4) : '—'}
                sub={t(G.logs.sumCoverage, {
                  cov: t(G.logs.coverage, { p: fmtPct(s.requests > 0 ? s.known_cost_requests / s.requests : null) }),
                  n: fmtInt(s.unknown_cost_requests),
                })}
              />
            </div>
          ) : (
            <div className="ops-note">{t(G.logs.needUser)}</div>
          )}
          <TableWrap
            head={
              <>
                <th>{t(G.logs.colTime)}</th>
                <th>{t(G.logs.colUser)}</th>
                <th>{t(G.logs.colModel)}</th>
                <th>{t(G.logs.colEndpoint)}</th>
                <th>{t(G.logs.colSource)}</th>
                <th>{t(G.logs.colDuration)}</th>
                <th>{t(G.logs.colInput)}</th>
                <th>{t(G.logs.colOutput)}</th>
                <th>{t(G.logs.colCached)}</th>
                <th>{t(G.logs.colCharge)}</th>
                <th>{t(G.logs.colUpstream)}</th>
                <th>{t(G.logs.colStatus)}</th>
                <th />
              </>
            }
            pager={
              <PageNav
                page={Math.floor(offset / PAGE) + 1}
                pages={pages}
                label={t(G.common.pageOf, { p: Math.floor(offset / PAGE) + 1, n: pages })}
                prev={t(G.common.prev)}
                next={t(G.common.next)}
                onPage={(p) => setOffset((p - 1) * PAGE)}
              />
            }
          >
            {listLoading && !list ? (
              <EmptyRow cols={13}>{t(G.common.loading)}</EmptyRow>
            ) : listError && !list ? (
              <EmptyRow cols={13}>{t(G.common.loadFailed, { msg: listError })}</EmptyRow>
            ) : rows.length === 0 ? (
              <EmptyRow cols={13}>{t(G.logs.empty)}</EmptyRow>
            ) : (
              rows.map((r) => (
                <LogRow
                  key={r.request_id}
                  row={r}
                  locale={locale}
                  onSource={(src) => apply({ ...draft, source: src })}
                  onDetail={() => setDetailId(r.request_id)}
                />
              ))
            )}
          </TableWrap>
        </>
      ) : (
        <Dashboard
          data={dash}
          loading={dashLoading}
          error={dashError}
          dim={dim}
          metric={metric}
          userFilter={applied.user.trim() !== ''}
          onDim={(d) => {
            setDim(d)
            remember(LS.dim, d)
          }}
          onMetric={(m) => {
            setMetric(m)
            remember(LS.metric, m)
          }}
          onViewModel={(key) => {
            // 上游路由的 key 形如「渠道/上游模型」，明细筛选取模型部分，后端按模型、上游模型、渠道三处模糊匹配
            const model = dim === 'upstream' ? key.slice(key.indexOf('/') + 1) : key
            pickView('list')
            apply({ ...draft, model })
          }}
          onPickSource={(src) => apply({ ...draft, source: src })}
          onPickUser={(u) => apply({ ...draft, user: u })}
        />
      )}

      <LogDetailModal requestId={detailId} onClose={() => setDetailId(null)} />
      <ExportDialog
        open={exportOpen}
        title={t(G.exportDialog.logsTitle)}
        note={t(G.exportDialog.current)}
        withBodies
        onSubmit={async (p) => {
          try {
            await doExport(p)
          } catch (e) {
            console.error('[logs] 导出失败', e)
            throw e
          }
        }}
        onClose={() => setExportOpen(false)}
      />
      <VendorDialog open={vendorOpen} user={applied.user.trim()} model={applied.model.trim()} onClose={() => setVendorOpen(false)} />
    </div>
  )
}

function LogRow({ row: r, locale, onSource, onDetail }: { row: LogRecord; locale: 'ko' | 'zh' | 'en'; onSource: (s: string) => void; onDetail: () => void }) {
  const { t } = useI18n()
  const money = useMoney()
  const ok = r.status_code > 0 && r.status_code < 400
  const discounted = r.list_cost !== null && r.list_cost > r.cost
  const route = [r.channel_id, r.upstream_model].filter(Boolean).join('/')
  return (
    <tr>
      <td style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at, locale)}</td>
      <td style={{ maxWidth: 150 }}>
        <div style={{ fontSize: 12, ...ellipsis }} title={r.account_name}>
          {r.account_name || t(G.common.accountFallback, { id: r.account_id.slice(0, 8) })}
        </div>
        <div className="mono" style={{ fontSize: 10, color: 'var(--text-tertiary)', ...ellipsis }} title={r.account_id}>
          {r.account_id}
        </div>
      </td>
      <td style={{ maxWidth: 200 }}>
        <div className="mono" style={{ fontSize: 11, ...ellipsis }} title={r.model}>
          {r.shell_display_name ? `${r.shell_display_name} (${r.model})` : r.model}
        </div>
        {route && (
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-tertiary)', ...ellipsis }} title={route}>
            {route}
          </div>
        )}
      </td>
      <td className="mono" style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
        {r.endpoint || '—'}
      </td>
      <td>
        {r.source ? (
          <span className="mono" style={{ fontSize: 10, cursor: 'pointer', color: 'var(--accent)' }} title={t(G.logs.sourceTip, { s: r.source })} onClick={() => onSource(r.source)}>
            {r.source}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td style={{ fontSize: 12 }}>
        {fmtLatency(r.duration_ms)}
        {r.queue_wait_ms >= 1000 && <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{t(G.logs.waited, { v: fmtLatency(r.queue_wait_ms) })}</div>}
      </td>
      <td style={{ fontSize: 12 }}>{fmtInt(r.input_tokens)}</td>
      <td style={{ fontSize: 12 }}>{fmtInt(r.output_tokens)}</td>
      <td style={{ fontSize: 12 }}>{fmtInt(r.cached_tokens)}</td>
      <td style={{ fontSize: 12 }}>
        {discounted ? (
          <>
            <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textDecoration: 'line-through' }}>{t(G.logs.listPrice, { v: money.petals(r.list_cost) })}</div>
            <div>{t(G.logs.paid, { v: money.petals(r.cost) })}</div>
            <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{t(G.logs.discountOf, { code: r.discount_code || t(G.logs.subsidy) })}</div>
          </>
        ) : (
          money.petals(r.cost)
        )}
        <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{money.usd(r.cost, 6)}</div>
      </td>
      <td className="mono" style={{ fontSize: 11 }}>
        {r.upstream_cost === null ? '—' : money.usd(r.upstream_cost, 6)}
      </td>
      <td>
        <span className={`badge ${ok ? 'badge-ok' : 'badge-off'}`}>{r.status_code || '—'}</span>
        {r.error_message && (
          <div style={{ fontSize: 10, color: 'var(--error-text)', maxWidth: 160, ...ellipsis }} title={r.error_message}>
            {r.error_message}
          </div>
        )}
      </td>
      <td>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onDetail}>
          {t(G.common.detail)}
        </button>
      </td>
    </tr>
  )
}
